import promiseRetry from '../retry';
import { isFunction } from '../type-check';

interface promiseRetryDecoratorArguments{
  settings: { retries: number | (() => number), timeout: ((retryCount: number) => number) | number; },
  onError?: (error: any, retry: () => void, reject: (reason?: any) => void, retryFuncArgs: any) => void,
  onRetrying?: (retryCount: number, stopRetry?: () => void) => void,
  onRetryFailed?: (err: Error) => void,
  context?: any;
}

const retryingMap: WeakMap<any, Map<string, {   stopRetry?: () => void; args: any[]}>> = new WeakMap();

/**
 * 装饰器函数：给异步函数增加重试
 * @param {Object} settings
 * @param {number} [settings.retries = 5]
 * @param {number} [settings.timeout = 2000]
 * @param {Function} [settings.onError]
 * @param {Function} [settings.onRetrying]
 * @param {Function} [settings.onRetryFailed]
 * @example
 * class Room {
 *    @addPromiseRetry({
 *      settings: { retries: 2000, timeout: 2000 },
 *      onError(error, retry, reject) {
 *        // 对 timeout 错误进行重试
 *        if (error.message?.includes('timeout')) {
 *          retry();
 *        } else {
 *          reject(error);
 *        }
 *      }
 *    })
 *    publish(...localTracks) {}
 *
 *    @stopPromiseRetry({ fnName: 'publish' })
 *    unpublish(...localTracks) {}
 *
 *    @stopPromiseRetry({ fnName: 'publish', validateArgs: false })
 *    leave() {}
 * }
 */
export function addPromiseRetry({ settings = { retries: 5, timeout: 2000 }, onError, onRetrying, onRetryFailed } : promiseRetryDecoratorArguments) {
  return function (_target: any, _name: string, descriptor: any) {
    const oldFn = promiseRetry({
      retryFunction: descriptor.value,
      settings,
      onError({ error, retry, reject, retryFuncArgs }) {
        if (onError) {
          onError.call(this, error, () => {
            if (retryingMap.get(_target)?.has(_name)) {
              retry();
            } else {
              reject(error);
            }
          }, reject, retryFuncArgs);
        } else {
          if (retryingMap.get(_target)?.has(_name)) {
            retry();
          } else {
            reject(error);
          }
        }
      },
      onRetrying(retryCount: number, stopRetry?: () => void) {
        if (isFunction(onRetrying)) {
          onRetrying.call(this, retryCount, stopRetry);
        }
        if (retryingMap.get(_target)?.has(_name)) {
          retryingMap.get(_target)!.get(_name)!.stopRetry = stopRetry;
        }
      },
      onRetryFailed
    });

    descriptor.value = function (...args: any) {
      const map = retryingMap.get(_target);
      if (map) {
        map.set(_name, { args });
      } else {
        retryingMap.set(_target, new Map([[_name, { args }]]));
      }
      return oldFn.apply(this, args).finally(() => retryingMap.get(_target)?.delete(_name));
    };
    return descriptor;
  };
}

export function removePromiseRetry({ fnName, callback, validateArgs = true }: { fnName: string, callback?: (...args: any) => void, validateArgs?: boolean }) {
  return function (_target: any, _name: any, descriptor: any) {
    const oldFn = descriptor.value;

    descriptor.value = function (...args: any[]) {
      if (retryingMap.get(_target)?.has(fnName)) {
        const { stopRetry, args: targetArgs } = retryingMap.get(_target)!.get(fnName)!;
        let isSameArgs = true;
        if (validateArgs) {
          for (const item of targetArgs) {
            if (!args.find(i => i === item)) {
              isSameArgs = false;
              break;
            }
          }
        }
        if (isSameArgs) {
          if (callback) {
            callback.apply(this, args);
          }
          if (stopRetry) stopRetry();
          retryingMap.get(_target)?.delete(fnName);
        }
      }

      return oldFn.apply(this, args);
    };
    return descriptor;
  };
}
